import {
  Component,
  ElementRef,
  Input,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, lastValueFrom, Observable } from 'rxjs';
import { Area } from 'src/app/models/area.model';
import { ChecklistItem } from 'src/app/models/checklist-item.model';
import { CustomerHistoryDataOut } from 'src/app/models/history.model';
import {
  AssetStatus,
  ClientType,
  CustomerCrudTable,
  CustomerType,
  ServiceType,
  TypeOfConnection,
} from 'src/app/models/customer.model';
import { Feeder, FeederType } from 'src/app/models/feeder.model';
import { Region } from 'src/app/models/region.model';
import { PermissionsEnum } from 'src/app/models/role.model';
import { Tariff } from 'src/app/models/tariff.model';
import { BPSAdminLevel } from 'src/app/models/user.model';
import { OptionObj } from 'src/app/models/util.model';
import { AreaService } from 'src/app/services/area.service';
import { CustomerService } from 'src/app/services/customer.service';
import { DtService } from 'src/app/services/dt.service';
import { Feeder11Service } from 'src/app/services/feeder11.service';
import { Feeder33Service } from 'src/app/services/feeder33.service';
import {
  AcLineSegmentService,
  AuthService,
  CimSubstationService,
  PermissionsService,
  SubGeographicalRegionService,
} from 'shared-lib';
import { RegionService } from 'src/app/services/region.service';
import { TariffService } from 'src/app/services/tariff.service';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslationUtils } from 'src/assets/i18n/translation.utils';
import { CustomerBillingSyncHistoryService } from 'src/app/services/customer-billing-sync-history.service';

enum CustomerCrudTab {
  Customers = 'Customers',
  ServiceRequest = 'ServiceRequest',
  NewEntry = 'NewEntry',
}

@Component({
  selector: 'crud-customer',
  templateUrl: './crud-customer.component.html',
  styleUrls: ['./crud-customer.component.scss'],
})
export class CrudCustomerComponent implements OnInit {
  constructor(
    private router: Router,
    private toastr: ToastrService,
    public permissionsService: PermissionsService,
    public authService: AuthService,
    private customerService: CustomerService,
    private regionService: RegionService,
    private areaService: AreaService,
    private tariffService: TariffService,
    private translationService: TranslationService,
    private translateService: TranslateService,
    private customerSyncHistoryService: CustomerBillingSyncHistoryService,
    private subGeographicalRegionService: SubGeographicalRegionService,
    private feeder11Service: Feeder11Service,
    private feeder33Service: Feeder33Service,
    private dtService: DtService,
    private acLineSegmentService: AcLineSegmentService,
    private cimSubstationService: CimSubstationService,
  ) {}

  @Input() searchText!: string;
  @Input() searchType!: string;

  @Input() utilityId: number = 0;
  @Input() bpsAdminLevel!: BPSAdminLevel;
  showDownload = false;
  showFormatOptions = false;
  public PermissionsEnum = PermissionsEnum;
  public currentPage: number = 1;
  public pageSize: number = 5;
  public count: number = 0;
  public showUpload: boolean = false;
  regions: Region[] = [];
  allAreas: Area[] = [];
  areas: Area[] = [];
  customers: CustomerCrudTable[] = [];
  actionLabel$!: Observable<string>;
  isEditMode: boolean = false;
  public CustomerCrudTab = CustomerCrudTab;
  selectedTab: CustomerCrudTab = CustomerCrudTab.Customers;
  isNewConnection: boolean = false;
  customerForDeleteId: number = 0;
  customerForDeleteRecord: string = '';
  buName: string = this.translationService.getByKeyFromStorage('BU');
  utName: string = this.translationService.getByKeyFromStorage('UT');
  contractNumber: string =
    this.translationService.getByKeyFromStorage('Contract number');
  selectedAreas: number[] = [];
  selectedRegions: number[] = [];

  historyItems: CustomerHistoryDataOut[] = [];
  geoRegions: any[] = [];
  allSubGeoRegions: any[] = [];
  subGeoRegions: any[] = [];
  selectedGeoRegionIds: number[] = [];
  selectedSubgeographicalRegions: number[] = [];

  feeders: Feeder[] = [];
  selectedFeeder: Feeder | null = null;
  dts: any[] = [];
  selectedDt: any = null;
  selectedDtId: number | null = null;

  acLines: any[] = [];
  selectedAcLine: any = null;
  substations: any[] = [];
  selectedSubstation: any = null;
  selectedSubstationId: number | null = null;

  @ViewChild('closeAddModal') closeAddModal: ElementRef;
  @ViewChild('closeDeleteModal') closeDeleteModal: ElementRef;

  tariff: number = 0;
  tariffs: Tariff[] = [];

  serviceType?: number;
  serviceTypeOptions = [
    { name: 'Large', value: ServiceType.Large },
    { name: 'Small', value: ServiceType.Small },
    { name: 'Prepaid', value: ServiceType.Prepaid },
    { name: 'AMR', value: ServiceType.AMR },
  ];

  status?: number;
  statusOptions = [
    { name: 'Pending setup', value: 0 },
    { name: 'Enumerated', value: 1 },
  ];
  sltStatus?: number;

  sltOptions: OptionObj[] = [];
  clientType?: number;
  clientTypeOptions = [
    { name: 'Company', value: ClientType.Company },
    {
      name: 'Embassy Foreign Institution',
      value: ClientType.EmbassyForeignInstitution,
    },
    { name: 'Individual', value: ClientType.Individual },
    { name: 'Government', value: ClientType.Government },
    { name: 'Assembly', value: ClientType.Assembly },
  ];

  customerType?: number;
  customerTypeOptions = [
    { name: 'Prepaid', value: CustomerType.Prepaid },
    { name: 'Postpaid', value: CustomerType.Postpaid },
  ];

  customerPhase?: number;
  customerPhaseOptions = [
    { name: 'SinglePhase', value: TypeOfConnection.SinglePhase },
    { name: 'ThreePhase', value: TypeOfConnection.ThreePhase },
  ];

  clicked: boolean = false;

  selectedFilters: ChecklistItem[] = [];
  isGhanaPostFieldsVisible: boolean = true;

  ngOnInit(): void {
    this.setFieldsVisibility();
    if (this.utilityId != 0) {
      this.getSltStatuses();
      this.setFilters();
      this.reloadTable();
    }

    this.updateActionLabel();
  }

  private updateActionLabel(): void {
    const key = this.isEditMode
      ? TranslationUtils.EDIT_KEY
      : TranslationUtils.NEW_KEY;
    this.actionLabel$ = this.translateService.stream(key);
  }

  setCustomerTab(tab: CustomerCrudTab) {
    this.selectedTab = tab;
    this.isNewConnection = tab === CustomerCrudTab.ServiceRequest;
    this.showUpload = false;
    this.showDownload = false;
    this.showFormatOptions = false;
    this.currentPage = 1;
    this.reloadTable();
  }

  private getIsNewConnectionFilter(): boolean | null {
    if (
      this.selectedTab === CustomerCrudTab.Customers ||
      this.selectedTab === CustomerCrudTab.NewEntry
    ) {
      return null;
    }

    return this.selectedTab === CustomerCrudTab.ServiceRequest;
  }

  private getIsNewFilter(): boolean | null {
    if (this.selectedTab === CustomerCrudTab.Customers) {
      return false;
    }

    return this.selectedTab === CustomerCrudTab.NewEntry ? true : null;
  }

  selectCustomer(id: number) {
    this.router.navigate([`/customers/crud-edit/${id}/${this.utilityId}`]);
    this.setFilterStorage();
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable();
  }

  setCustomerForDeleteId(customer: any) {
    this.customerForDeleteId = customer.id;
    this.customerForDeleteRecord = customer.accountNumber;
  }

  async reloadTable(page: any = null) {
    if (page) {
      this.currentPage = page;
    }

    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId,
    };

    await this.getAllCustomers(dataIn);
  }

  getFilterObject() {
    let obj = {
      search: this.searchText,
      isNewConnection: this.getIsNewConnectionFilter(),
      isNew: this.getIsNewFilter(),
      tariffId: this.tariff != 0 ? this.tariff : null,
      serviceType: this.serviceType ? this.serviceType : null,
      customerStatus: this.status != null ? this.status : null,
      clientType: this.clientType ? this.clientType : null,
      customerType: this.customerType ? this.customerType : null,
      sltStatus: this.sltStatus ? this.sltStatus : null,
      customerPhase: this.customerPhase ? this.customerPhase : null,
      selectedAreas: this.selectedAreas.length > 0 ? this.selectedAreas : null,
      selectedRegions:
        this.selectedRegions.length > 0 ? this.selectedRegions : null,
      selectedGeoRegions:
        this.selectedGeoRegionIds.length > 0 ? this.selectedGeoRegionIds : null,
      selectedSubgeographicalRegions:
        this.selectedSubgeographicalRegions.length > 0
          ? this.selectedSubgeographicalRegions
          : null,
      searchType: this.searchType,
      // a specific Dt (or Substation) always wins; otherwise, if only the
      // parent Feeder (or AcLine) is picked, filter by every Dt/Substation under it
      dtId: this.selectedDtId || null,
      dtIds: this.selectedDtId
        ? [this.selectedDtId]
        : this.selectedFeeder
          ? this.dts.map((d) => d.key)
          : null,
      selectedSubstations: this.selectedSubstationId
        ? [this.selectedSubstationId]
        : this.selectedAcLine
          ? this.substations.map((s) => s.id)
          : null,
      selectedFeederId: this.selectedFeeder?.id ?? null,
      selectedAcLineId: this.selectedAcLine?.id ?? null,
    };

    return obj;
  }
  setFilterStorage() {
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId,
    };
    localStorage.setItem(
      'filter',
      JSON.stringify({ type: 'CustomerCrud', filter: obj }),
    );
  }
  filter() {
    this.currentPage = 1;
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: {
        search: this.searchText,
        isNewConnection: this.getIsNewConnectionFilter(),
        isNew: this.getIsNewFilter(),
        searchType: this.searchType,
      },
      utilityId: this.utilityId,
    };
    this.getAllCustomers(dataIn);
  }

  private async getAllCustomers(dataIn: any) {
    try {
      const response = await lastValueFrom(
        this.customerService.getAllCrud(dataIn),
      );

      const responseData = response.body;
      if (responseData?.status === 200 || responseData?.status === 'OK') {
        this.customers = responseData.data.data;
        this.count = responseData.data.count ?? this.count;
        this.createFilterCards();
        localStorage.removeItem('filter');
      } else {
        this.toastr.error(responseData?.message);
      }
    } catch (err) {
      this.toastr.error('Error occured');
      console.error(err);
    }
  }

  deleteCustomer() {
    this.customerService
      .delete(this.customerForDeleteId, this.customerForDeleteRecord)
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          this.closeDeleteModal?.nativeElement?.click();
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.pageChange(1);
            this.toastr.success(responseData?.message);
          } else {
            this.toastr.error(responseData?.message);
          }
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
  }

  viewHistory(id: number) {
    this.customerService.getHistory(id).subscribe({
      next: (response) => {
        this.historyItems = response?.body?.data ?? [];
      },
    });
  }

  private getRegions() {
    this.regionService.getAllForSelectNoUser(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data;
          this.setCheckedRegions();

          this.getAreas();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getAreas() {
    this.areaService.getAllForOptions(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allAreas = responseData.data;
          this.areas = this.allAreas.filter((area) =>
            this.regions.some(
              (region) => region.id == area.regionId && region.checked,
            ),
          );
          this.setCheckedAreas();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  getFeeders() {
    forkJoin({
      f11: this.feeder11Service.getAllForSelect(this.utilityId),
      f33: this.feeder33Service.getAllForSelect(this.utilityId),
    }).subscribe({
      next: ({ f11, f33 }) => {
        const feeders11 = (f11.body?.data ?? []).map(
          (x: any) => new Feeder({ ...x, feederType: FeederType.KV11 }),
        );
        const feeders33 = (f33.body?.data ?? []).map(
          (x: any) => new Feeder({ ...x, feederType: FeederType.KV33 }),
        );
        this.feeders = [...feeders11, ...feeders33];
        this.setCheckedFeederAndDt();
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  selectFeederFilter(e: any) {
    const feederId = e.target.value !== '' ? +e.target.value : null;
    this.selectedFeeder = feederId
      ? this.feeders.find((f) => f.id === feederId) ?? null
      : null;
    this.selectedDt = null;
    this.selectedDtId = null;
    this.dts = [];
    if (this.selectedFeeder) {
      this.loadDtsForFeeder(this.selectedFeeder);
    }
    this.createFilterCards();
  }

  private loadDtsForFeeder(feeder: Feeder, onLoaded?: () => void) {
    this.dtService
      .getAllForSelectByFeeder(feeder.id, feeder.feederType.toString())
      .subscribe({
        next: (response) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.dts = responseData.data ?? [];
          }
          if (onLoaded) onLoaded();
        },
        error: (_) => {
          this.toastr.error('Error occured');
        },
      });
  }

  selectDtFilter(e: any) {
    const dtKey = e.target.value !== '' ? +e.target.value : null;
    this.selectedDt = dtKey
      ? this.dts.find((d) => d.key === dtKey) ?? null
      : null;
    this.selectedDtId = this.selectedDt?.key ?? null;
    this.createFilterCards();
  }

  private setCheckedFeederAndDt() {
    const filter = localStorage.getItem('filter');
    if (!filter) return;
    const filterObject = JSON.parse(filter);
    if (filterObject.type != 'CustomerCrud') return;
    const feederId = filterObject.filter.filterParams.selectedFeederId;
    if (!feederId) return;
    const feeder = this.feeders.find((f) => f.id === feederId);
    if (!feeder) return;
    this.selectedFeeder = feeder;
    this.loadDtsForFeeder(feeder, () => {
      if (this.selectedDtId) {
        this.selectedDt =
          this.dts.find((x) => x.key === this.selectedDtId) ?? null;
      }
    });
  }

  getTariffs() {
    this.tariffService.getAllForSelect(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.tariffs = responseData.data;
          this.removeFilterStorage();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  getSltStatuses() {
    this.customerService.getSltStatusesForOptions().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.sltOptions = responseData.data;
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  selectTariff(e: any) {
    this.tariff = +e.target.value;
    this.createFilterCards();
  }

  selectCustomerPhase(e: any) {
    if (e.target.value != '') {
      this.customerPhase = e.target.value;
    } else {
      this.customerPhase = undefined;
    }
    this.createFilterCards();
  }

  selectClientType(e: any) {
    if (e.target.value != '') {
      this.clientType = e.target.value;
    } else {
      this.clientType = undefined;
    }
    this.createFilterCards();
  }

  selectCustomerType(e: any) {
    if (e.target.value != '') {
      this.customerType = e.target.value;
    } else {
      this.customerType = undefined;
    }
    this.createFilterCards();
  }

  selectServiceType(e: any) {
    if (e.target.value != '') {
      this.serviceType = e.target.value;
    } else {
      this.serviceType = undefined;
    }
    this.createFilterCards();
  }

  selectStatus(e: any) {
    if (e.target.value != '') {
      this.status = e.target.value;
    } else {
      this.status = undefined;
    }
    this.createFilterCards();
  }

  selectSltStatus(e: any) {
    if (e.target.value != '') {
      this.sltStatus = parseInt(e.target.value);
    } else {
      this.sltStatus = undefined;
    }
    this.createFilterCards();
  }

  selectRegion(e: any) {
    //list of areas for selected regions
    this.areas = this.allAreas.filter((area) =>
      this.regions.some(
        (region) => region.id == area.regionId && region.checked,
      ),
    );
    this.allAreas.forEach((area) => {
      if (!this.areas.some((a) => a.id === area.id)) {
        area.checked = false; //uncheck areas for unchecked regions
      }
    });
    this.createFilterCards();
  }

  selectArea(e: any) {
    this.createFilterCards();
  }

  removeFilter(item: ChecklistItem) {
    if (item.property == this.buName) {
      this.regions.forEach((region) => {
        if (region.name === item.selectedValue) region.checked = false;
      });
      this.selectRegion(event);
      this.clicked = !this.clicked;
    } else if (item.property == this.utName) {
      this.areas.forEach((area) => {
        if (area.name === item.selectedValue) area.checked = false;
      });
      this.subGeoRegions.forEach((sgr) => {
        if (sgr.name === item.selectedValue) sgr.checked = false;
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Tariff') {
      this.tariff = 0;
    } else if (item.property == 'Client type') {
      this.clientType = undefined;
    } else if (item.property == 'Customer type') {
      this.customerType = undefined;
    } else if (item.property == 'Service type') {
      this.serviceType = undefined;
    } else if (item.property == 'Status') {
      this.status = undefined;
    } else if (item.property == 'Slt status') {
      this.sltStatus = undefined;
    } else if (item.property == 'Customer phase') {
      this.customerPhase = undefined;
    } else if (item.property == 'Dt' || item.property == 'Feeder') {
      this.selectedFeeder = null;
      this.selectedDt = null;
      this.selectedDtId = null;
      this.dts = [];
    } else if (item.property == 'Substation' || item.property == 'AC Line') {
      this.selectedAcLine = null;
      this.selectedSubstation = null;
      this.selectedSubstationId = null;
      this.substations = [];
    }
    this.createFilterCards();

    if (this.selectedFilters.length == 0) {
      this.regions.forEach((region) => {
        region.checked = false;
      });
      this.reloadTable(1);
    }
  }

  resetFilter(e: any) {
    this.selectedFilters = [];

    this.regions.forEach((region) => {
      region.checked = false;
    });

    this.areas.forEach((area) => {
      area.checked = false;
    });
    this.geoRegions.forEach((g) => {
      g.checked = false;
    });
    this.allSubGeoRegions.forEach((s) => {
      s.checked = false;
    });
    this.subGeoRegions = [];
    this.selectedAreas = [];
    this.selectedRegions = [];
    this.selectedGeoRegionIds = [];
    this.selectedSubgeographicalRegions = [];

    this.selectedFeeder = null;
    this.selectedDt = null;
    this.selectedDtId = null;
    this.dts = [];
    this.selectedAcLine = null;
    this.selectedSubstation = null;
    this.selectedSubstationId = null;
    this.substations = [];

    this.tariff = 0;
    this.serviceType = undefined;
    this.status = undefined;
    this.sltStatus = undefined;
    this.clientType = undefined;
    this.customerType = undefined;
    this.customerPhase = undefined;

    this.clicked = !this.clicked;

    this.reloadTable(1);
  }

  createFilterCards() {
    this.selectedFilters = [];

    let selectedAreas = this.areas.filter((x) => x.checked);
    this.selectedAreas = this.areas.filter((x) => x.checked).map((x) => x.id);
    this.selectedRegions = this.regions
      .filter((x) => x.checked)
      .map((x) => x.id);

    const selectedGeos = this.geoRegions.filter((x) => x.checked);
    this.selectedGeoRegionIds = selectedGeos.map((x) => x.id);
    const selectedSubGeos = this.subGeoRegions.filter((x) => x.checked);
    this.selectedSubgeographicalRegions = selectedSubGeos.map((x) => x.id);

    //areas (legacy)
    for (var i = 0; i < selectedAreas.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(this.utName, selectedAreas[i].name),
      );
    }

    //cim sub-geo regions
    for (var i = 0; i < selectedSubGeos.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(this.utName, selectedSubGeos[i].name),
      );
    }

    //tariff
    if (this.tariff != 0) {
      let tariffName = this.tariffs.filter((x) => x.id == this.tariff)[0].name;
      this.selectedFilters.push(new ChecklistItem('Tariff', tariffName));
    }

    //client type
    if (this.clientType) {
      var clientTypeName = ClientType[this.clientType!];
      this.selectedFilters.push(
        new ChecklistItem('Client type', clientTypeName),
      );
    }

    //customer type
    if (this.customerType) {
      var customerTypeName = CustomerType[this.customerType!];
      this.selectedFilters.push(
        new ChecklistItem('Customer type', customerTypeName),
      );
    }

    //service type
    if (this.serviceType) {
      var serviceTypeName = ServiceType[this.serviceType!];
      this.selectedFilters.push(
        new ChecklistItem('Service type', serviceTypeName),
      );
    }

    //slt
    if (this.sltStatus) {
      var sltStatusName = this.sltOptions.find(
        (x) => x.key == this.sltStatus,
      )!.value;
      this.selectedFilters.push(new ChecklistItem('Slt status', sltStatusName));
    }

    //status
    if (this.status != null) {
      var statusName = this.statusOptions.find((x) => x.value == this.status)!.name;
      this.selectedFilters.push(new ChecklistItem('Status', statusName));
    }

    //customer phase
    if (this.customerPhase) {
      var customerPhaseName =
        this.customerPhaseOptions[this.customerPhase!].name;
      this.selectedFilters.push(
        new ChecklistItem('Customer phase', customerPhaseName),
      );
    }

    //dt takes priority over feeder once picked, since it's the more specific filter
    if (this.selectedDt) {
      this.selectedFilters.push(new ChecklistItem('Dt', this.selectedDt.value));
    } else if (this.selectedFeeder) {
      this.selectedFilters.push(
        new ChecklistItem('Feeder', this.selectedFeeder.name),
      );
    }

    //substation takes priority over ac line once picked, since it's the more specific filter
    if (this.selectedSubstation) {
      this.selectedFilters.push(
        new ChecklistItem('Substation', this.selectedSubstation.name),
      );
    } else if (this.selectedAcLine) {
      this.selectedFilters.push(
        new ChecklistItem('AC Line', this.selectedAcLine.name),
      );
    }
  }
  stopPropagation(event: Event) {
    event.stopPropagation();
  }
  setFilters() {
    var filter = localStorage.getItem('filter');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'CustomerCrud') return;
    this.currentPage = filterObject.filter.pageInfo.page;
    this.pageSize = filterObject.filter.pageInfo.pageSize;
    this.count = filterObject.filter.pageInfo.count;
    this.searchText = filterObject.filter.filterParams.search;
    this.tariff = filterObject.filter.filterParams.tariffId
      ? filterObject.filter.filterParams.tariffId
      : 0;
    this.serviceType = filterObject.filter.filterParams.serviceType
      ? filterObject.filter.filterParams.serviceType
      : undefined;
    this.status =
      filterObject.filter.filterParams.customerStatus != null
        ? filterObject.filter.filterParams.customerStatus
        : undefined;
    this.isNewConnection = filterObject.filter.filterParams.isNewConnection;
    if (filterObject.filter.filterParams.isNew === true) {
      this.selectedTab = CustomerCrudTab.NewEntry;
      this.isNewConnection = false;
    } else if (filterObject.filter.filterParams.isNewConnection === true) {
      this.selectedTab = CustomerCrudTab.ServiceRequest;
      this.isNewConnection = true;
    } else {
      this.selectedTab = CustomerCrudTab.Customers;
      this.isNewConnection = false;
    }
    this.sltStatus = filterObject.filter.filterParams.sltStatus
      ? filterObject.filter.filterParams.sltStatus
      : undefined;
    this.clientType = filterObject.filter.filterParams.clientType
      ? filterObject.filter.filterParams.clientType
      : undefined;
    this.customerType = filterObject.filter.filterParams.customerType
      ? filterObject.filter.filterParams.customerType
      : undefined;
    this.customerPhase = filterObject.filter.filterParams.customerPhase
      ? filterObject.filter.filterParams.customerPhase
      : undefined;
    this.selectedAreas = filterObject.filter.filterParams.selectedAreas
      ? filterObject.filter.filterParams.selectedAreas
      : [];
    this.selectedRegions = filterObject.filter.filterParams.selectedRegions
      ? filterObject.filter.filterParams.selectedRegions
      : [];
    this.selectedGeoRegionIds = filterObject.filter.filterParams
      .selectedGeoRegions
      ? filterObject.filter.filterParams.selectedGeoRegions
      : [];
    this.selectedSubgeographicalRegions = filterObject.filter.filterParams
      .selectedSubgeographicalRegions
      ? filterObject.filter.filterParams.selectedSubgeographicalRegions
      : [];
    this.selectedDtId = filterObject.filter.filterParams.dtId
      ? filterObject.filter.filterParams.dtId
      : null;
    this.selectedSubstationId = filterObject.filter.filterParams
      .selectedSubstations?.length
      ? filterObject.filter.filterParams.selectedSubstations[0]
      : null;
  }

  setCheckedAreas() {
    var filter = localStorage.getItem('filter');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'CustomerCrud') return;
    if (filterObject.filter.filterParams.selectedAreas) {
      this.selectedAreas = filterObject.filter.filterParams.selectedAreas;
      this.areas = this.allAreas.filter((area) =>
        this.regions.some(
          (region) => region.id == area.regionId && region.checked,
        ),
      );
      this.areas.forEach((element) => {
        if (this.selectedAreas.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }
  setCheckedRegions() {
    var filter = localStorage.getItem('filter');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'CustomerCrud') return;
    if (filterObject.filter.filterParams.selectedRegions) {
      this.selectedRegions = filterObject.filter.filterParams.selectedRegions;
      this.regions.forEach((element) => {
        if (this.selectedRegions.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['utilityId']) {
      if (this.utilityId != 0) {
        this.getTariffs();
        if (this.authService.hasCimDataIntegration) {
          this.getSubGeoRegionsAndDeriveGeo();
          this.getAcLines();
        } else {
          this.getRegions();
          this.getFeeders();
        }
      }
    }
  }

  private getSubGeoRegionsAndDeriveGeo() {
    this.subGeographicalRegionService.getAllForMappings().subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allSubGeoRegions = (responseData.data ?? []).map((x: any) => ({
            id: x.id,
            name: x.name,
            checked: false,
            geographicalRegionId: x.geographicalRegion?.id,
          }));

          const seen = new Set<number>();
          this.geoRegions = (responseData.data ?? [])
            .filter(
              (x: any) =>
                x.geographicalRegion?.id &&
                !seen.has(x.geographicalRegion.id) &&
                seen.add(x.geographicalRegion.id),
            )
            .map((x: any) => ({
              id: x.geographicalRegion.id,
              name: x.geographicalRegion.name,
              checked: false,
            }));

          this.setCheckedGeoRegions();
          this.rebuildSubGeoRegions();
          this.setCheckedSubGeoRegions();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status != 204) {
          this.toastr.error(responseData?.message);
        }
      },
      error: () => this.toastr.error('Error occured'),
    });
  }

  private rebuildSubGeoRegions() {
    const checkedGeoIds = this.geoRegions
      .filter((g) => g.checked)
      .map((g) => g.id);
    this.subGeoRegions =
      checkedGeoIds.length > 0
        ? this.allSubGeoRegions.filter((s) =>
            checkedGeoIds.includes(s.geographicalRegionId),
          )
        : [];
    this.allSubGeoRegions.forEach((s) => {
      if (!this.subGeoRegions.some((x) => x.id === s.id)) s.checked = false;
    });
  }

  selectGeoRegion(_: any) {
    this.rebuildSubGeoRegions();
    this.createFilterCards();
  }

  selectSubGeoRegion(_: any) {
    this.createFilterCards();
  }

  setCheckedGeoRegions() {
    const filter = localStorage.getItem('filter');
    if (!filter) return;
    const filterObject = JSON.parse(filter);
    if (filterObject.type != 'CustomerCrud') return;
    if (filterObject.filter.filterParams.selectedGeoRegions) {
      this.selectedGeoRegionIds =
        filterObject.filter.filterParams.selectedGeoRegions;
      this.geoRegions.forEach((el) => {
        el.checked = this.selectedGeoRegionIds.includes(el.id);
      });
    }
  }

  setCheckedSubGeoRegions() {
    const filter = localStorage.getItem('filter');
    if (!filter) return;
    const filterObject = JSON.parse(filter);
    if (filterObject.type != 'CustomerCrud') return;
    if (filterObject.filter.filterParams.selectedSubgeographicalRegions) {
      this.selectedSubgeographicalRegions =
        filterObject.filter.filterParams.selectedSubgeographicalRegions;
      this.subGeoRegions.forEach((el) => {
        el.checked = this.selectedSubgeographicalRegions.includes(el.id);
      });
    }
  }

  getAcLines() {
    this.acLineSegmentService.getAllACLinesCached({ voltageClasses: [] }).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.acLines = responseData.data ?? [];
          this.setCheckedAcLineAndSubstation();
        } else if (response?.status != 204) {
          this.toastr.error(responseData?.message);
        }
      },
      error: () => this.toastr.error('Error occured'),
    });
  }

  selectAcLineFilter(e: any) {
    const acLineId = e.target.value !== '' ? +e.target.value : null;
    this.selectedAcLine = acLineId
      ? this.acLines.find((a) => a.id === acLineId) ?? null
      : null;
    this.selectedSubstation = null;
    this.selectedSubstationId = null;
    this.substations = [];
    if (this.selectedAcLine) {
      this.loadSubstationsForAcLine(this.selectedAcLine.id);
    }
    this.createFilterCards();
  }

  private loadSubstationsForAcLine(acLineId: number, onLoaded?: () => void) {
    this.cimSubstationService
      .getForSelect({ filterText: '', acLineId, take: 1000 })
      .subscribe({
        next: (response: any) => {
          const responseData = response.body;
          if (responseData?.status === 200 || responseData?.status === 'OK') {
            this.substations = responseData.data ?? [];
          }
          if (onLoaded) onLoaded();
        },
        error: () => this.toastr.error('Error occured'),
      });
  }

  selectSubstationFilter(e: any) {
    const substationId = e.target.value !== '' ? +e.target.value : null;
    this.selectedSubstation = substationId
      ? this.substations.find((s) => s.id === substationId) ?? null
      : null;
    this.selectedSubstationId = this.selectedSubstation?.id ?? null;
    this.createFilterCards();
  }

  private setCheckedAcLineAndSubstation() {
    const filter = localStorage.getItem('filter');
    if (!filter) return;
    const filterObject = JSON.parse(filter);
    if (filterObject.type != 'CustomerCrud') return;
    const acLineId = filterObject.filter.filterParams.selectedAcLineId;
    if (!acLineId) return;
    const acLine = this.acLines.find((a) => a.id === acLineId);
    if (!acLine) return;
    this.selectedAcLine = acLine;
    this.loadSubstationsForAcLine(acLine.id, () => {
      if (this.selectedSubstationId) {
        this.selectedSubstation =
          this.substations.find((x) => x.id === this.selectedSubstationId) ?? null;
      }
    });
  }

  download(format: string) {
    this.showFormatOptions = false;
    var obj = this.getFilterObject();
    this.customerService.downloadCrud(obj, format).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  downloadSyncHistory() {
    this.customerSyncHistoryService.download().subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  downloadCrudUnfiltered(downloadType: number) {
    this.customerService.downloadCrudUnfiltered(downloadType).subscribe({
      next: (response) => {
        const data = response;
        if (data?.status === 'OK' || data?.status === '200') {
          window.location.href = data.data;
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  downloadFeederBandMisalignmentReport() {
    this.customerService.getLatestFeederBandAlignmentReport().subscribe({
      next: (response) => {
        const data = response;
        if (data?.status === 'OK' || data?.status === '200') {
          if (data.data?.blobUrl && data.data.misalignedCount > 0) {
            window.location.href = data.data.blobUrl;
          } else {
            this.toastr.info('No records with misaligned feeder band.');
          }
        } else if (data?.status === 'Pending' || data?.status === '600') {
          this.toastr.info(data?.message || '');
        } else this.toastr.error(data?.message || 'Request failed');
      },
      error: (error) => console.log(error),
    });
  }

  removeFilterStorage() {
    if (this.tariffs.length === 0) return;
    if (this.authService.hasCimDataIntegration) {
      if (this.allSubGeoRegions.length > 0)
        localStorage.removeItem('filterCustomer');
    } else {
      if (this.allAreas.length > 0 && this.regions.length > 0)
        localStorage.removeItem('filterCustomer');
    }
  }

  setFieldsVisibility() {
    let notVisibleFields: any = localStorage.getItem('notVisibleFields');

    if (!notVisibleFields) {
      return;
    }

    notVisibleFields = JSON.parse(notVisibleFields);
    this.isGhanaPostFieldsVisible = notVisibleFields.find(
      (el: any) => el === 'ghanaPostCode',
    )
      ? false
      : true;
  }

  async applyFilter(item: ChecklistItem) {
    try {
      await this.reloadTable(1);
    } catch (err) {
      console.error('Error while loading data.', err);
    }
  }
}
